/**
 * Feature Request Service
 * Handles all feature request API calls
 */

import {
  FeatureRequest,
  CreateFeatureRequestRequest,
  UpdateFeatureRequestStatusRequest,
  ListFeatureRequestsParams,
  AdminListFeatureRequestsParams,
} from '@creatoros/shared';

import { apiClient, buildQueryParams, ApiResponse } from './api.client';
import { unwrapListResponse } from './api.unwrap';

/**
 * Response types
 */
interface FeatureRequestResponse {
  success: boolean;
  data: FeatureRequest;
  userVote?: 'up' | 'down' | null;
  timestamp: string;
}

interface VoteResponse {
  success: boolean;
  data: {
    vote: {
      id: string;
      voteType: 'up' | 'down';
    };
    featureRequest: FeatureRequest;
  };
  timestamp: string;
}

/**
 * Feature Request Service Class
 */
class FeatureRequestService {
  private readonly basePath = '/api/feature-requests';
  private readonly adminPath = '/api/management/feature-requests'; // Changed to match management pattern

  // ==========================================
  // User Endpoints
  // ==========================================

  /**
   * List approved feature requests (public view)
   */
  async list(params: ListFeatureRequestsParams = {}): Promise<{
    featureRequests: FeatureRequest[];
    userVotes: Record<string, 'up' | 'down'>;
    continuationToken?: string;
  }> {
    const queryParams = buildQueryParams(params as unknown as Record<string, unknown>);

    const response = await apiClient.get(this.basePath, {
      params: queryParams,
    });

    const unwrapped = unwrapListResponse<FeatureRequest>(response.data);
    return {
      featureRequests: unwrapped.data,
      userVotes: unwrapped.userVotes || {},
      continuationToken: unwrapped.continuationToken,
    };
  }

  /**
   * List current user's own feature requests
   */
  async listMyRequests(params: ListFeatureRequestsParams = {}): Promise<{
    featureRequests: FeatureRequest[];
    continuationToken?: string;
  }> {
    const queryParams = buildQueryParams(params as unknown as Record<string, unknown>);

    const response = await apiClient.get(
      `${this.basePath}-my`, // Changed from /my to /-my to match backend route change
      {
        params: queryParams,
      }
    );

    const unwrapped = unwrapListResponse<FeatureRequest>(response.data);
    return {
      featureRequests: unwrapped.data,
      continuationToken: unwrapped.continuationToken,
    };
  }

  /**
   * Get a single feature request by ID
   */
  async getById(id: string): Promise<{
    featureRequest: FeatureRequest;
    userVote: 'up' | 'down' | null;
  }> {
    const response = await apiClient.get<FeatureRequestResponse>(`${this.basePath}/${id}`);

    if (response.data.success && response.data.data) {
      return {
        featureRequest: response.data.data,
        userVote: response.data.userVote || null,
      };
    }

    throw new Error('Failed to fetch feature request');
  }

  /**
   * Create a new feature request
   */
  async create(data: CreateFeatureRequestRequest): Promise<FeatureRequest> {
    const response = await apiClient.post<FeatureRequestResponse>(this.basePath, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to create feature request');
  }

  /**
   * Vote on a feature request
   */
  async vote(id: string, voteType: 'up' | 'down'): Promise<FeatureRequest> {
    const response = await apiClient.post<VoteResponse>(`${this.basePath}/${id}/vote`, {
      voteType,
    });

    if (response.data.success && response.data.data) {
      return response.data.data.featureRequest;
    }

    throw new Error('Failed to vote');
  }

  /**
   * Remove vote from a feature request
   */
  async removeVote(id: string): Promise<FeatureRequest> {
    const response = await apiClient.delete<ApiResponse<{ featureRequest: FeatureRequest }>>(
      `${this.basePath}/${id}/vote`
    );

    if (response.data.success && response.data.data) {
      return response.data.data.featureRequest;
    }

    throw new Error('Failed to remove vote');
  }

  // ==========================================
  // Admin Endpoints
  // ==========================================

  /**
   * Admin: List all feature requests with optional status filter
   */
  async adminList(params: AdminListFeatureRequestsParams = {}): Promise<{
    featureRequests: FeatureRequest[];
    continuationToken?: string;
  }> {
    const queryParams = buildQueryParams(params as unknown as Record<string, unknown>);

    const response = await apiClient.get(this.adminPath, {
      params: queryParams,
    });

    const unwrapped = unwrapListResponse<FeatureRequest>(response.data);
    return {
      featureRequests: unwrapped.data,
      continuationToken: unwrapped.continuationToken,
    };
  }

  /**
   * Admin: Update feature request status
   */
  async adminUpdateStatus(
    id: string,
    data: UpdateFeatureRequestStatusRequest
  ): Promise<FeatureRequest> {
    const response = await apiClient.patch<FeatureRequestResponse>(
      `${this.adminPath}/${id}/status`,
      data
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to update status');
  }

  /**
   * Admin: Add response to feature request
   */
  async adminRespond(id: string, response: string): Promise<FeatureRequest> {
    const apiResponse = await apiClient.post<FeatureRequestResponse>(
      `${this.adminPath}/${id}/respond`,
      { response }
    );

    if (apiResponse.data.success && apiResponse.data.data) {
      return apiResponse.data.data;
    }

    throw new Error('Failed to add response');
  }

  /**
   * Admin: Delete feature request
   */
  async adminDelete(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`${this.adminPath}/${id}`);

    if (!response.data.success) {
      throw new Error('Failed to delete feature request');
    }
  }
}

/**
 * Export singleton instance
 */
export const featureRequestService = new FeatureRequestService();

export default featureRequestService;
